import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { updateServiceCost } from '@/modules/Report/components/ServiceCosts/services'
import {
    ServiceCost,
    UpdateServiceCostRequest,
} from '@/modules/Report/components/ServiceCosts/types'
import { ModalProps } from '@/types/shared'
import { yupResolver } from '@hookform/resolvers/yup'
import { useEffect, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Button, Dialog, FormItem, Typogaphy } from 'vnetwork-ui'
import * as yup from 'yup'

export interface UpdateServiceCostModalProps extends ModalProps {
    serviceCost: ServiceCost
    refetch?: () => void
}

interface FormData {
    cost_amount: number
    currency: string
    notes?: string
    description?: string
}

const UpdateServiceCostModal: React.FC<UpdateServiceCostModalProps> = ({
    open,
    onClose,
    serviceCost,
    refetch,
}) => {
    const { t } = useTranslation()

    // Form schema - only editable fields
    const schema = useMemo(
        () =>
            yup.object().shape({
                cost_amount: yup
                    .number()
                    .positive(t('must_be_positive'))
                    .required(t('required')),
                currency: yup.string().required(t('required')),
                notes: yup.string(),
                description: yup.string(),
            }),
        [t]
    )

    const defaultValues: FormData = useMemo(
        () => ({
            cost_amount: serviceCost.cost_amount,
            currency: serviceCost.currency,
            notes: serviceCost.notes || '',
            description: serviceCost.description || '',
        }),
        [serviceCost]
    )

    const formControl = useForm<FormData>({
        defaultValues,
        resolver: yupResolver(schema),
    })

    const { control, handleSubmit, reset } = formControl

    // Reset form when serviceCost changes
    useEffect(() => {
        if (open) {
            reset(defaultValues)
        }
    }, [open, serviceCost, reset, defaultValues])

    // Update mutation
    const { mutate: update, isPending } = useMutationWithHandler(
        {
            mutationFn: ({
                id,
                data,
            }: {
                id: number
                data: UpdateServiceCostRequest
            }) => updateServiceCost(id, data),
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.REPORTS_BI_SERVICE_COSTS,
                QUERY_KEYS.REPORTS_BI_REVENUE_COST,
            ],
            onSuccess: () => {
                refetch?.()
                onClose()
            },
        }
    )

    const onSubmit = (values: FormData) => {
        update({
            id: serviceCost.id,
            data: {
                cost_amount: values.cost_amount,
                currency: values.currency,
                notes: values.notes,
                description: values.description,
            },
        })
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title={t('edit_service_cost')}
            maxWidth="md"
        >
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 p-4">
                {/* Display read-only info */}
                <div className="bg-gray-50 p-4 rounded-md space-y-2">
                    <div className="flex justify-between">
                        <Typogaphy className="text-gray-600">
                            {t('cost_type')}:
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {serviceCost.cost_type === 'shared'
                                ? t('shared_cost')
                                : t('specific_cost')}
                        </Typogaphy>
                    </div>
                    {serviceCost?.product_group_name && (
                        <div className="flex justify-between">
                            <Typogaphy className="text-gray-600">
                                {t('product_group_name')}:
                            </Typogaphy>
                            <Typogaphy className="font-medium">
                                {serviceCost.product_group_name}
                            </Typogaphy>
                        </div>
                    )}
                    <div className="flex justify-between">
                        <Typogaphy className="text-gray-600">
                            {t('cost_month')}:
                        </Typogaphy>
                        <Typogaphy className="font-medium">
                            {serviceCost.cost_month}
                        </Typogaphy>
                    </div>
                </div>

                {/* Editable fields */}
                <FormItem
                    control={control}
                    name="cost_amount"
                    type="number"
                    label={t('cost_amount')}
                />
                <FormItem
                    control={control}
                    name="notes"
                    type="textarea"
                    label={t('notes')}
                />

                <FormItem
                    control={control}
                    name="description"
                    type="textarea"
                    label={t('description')}
                />

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isPending}
                        loading={isPending}
                    >
                        {t('update')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default UpdateServiceCostModal
